import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { keyPress } from "../utils/keyPress";
import { v4 as uuidv4 } from "uuid";
import { Dropdown, DropdownOptions, toCheckIfDisplayed, } from "../dropdown/Dropdown";
import { createUseStyles } from "react-jss";
import { THEME } from "../theming/theming";
export const TableDropdown = ({ dropdown, item, tableId, rowId, dataId, dataTestId }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const handleClick = useCallback((event) => {
        if (dropdownRef.current) {
            if (!dropdownRef.current.contains(event.target)) {
                setTimeout(() => setIsDropdownOpen(false), 100);
            }
        }
    }, [setIsDropdownOpen]);
    const keyFunctions = useCallback((event) => {
        if (event.keyCode === keyPress.ESCAPE) {
            setIsDropdownOpen(false);
        }
    }, [setIsDropdownOpen]);
    useEffect(() => {
        if (isDropdownOpen) {
            document.addEventListener("keydown", keyFunctions, false);
            document.addEventListener("mousedown", handleClick);
        }
        return () => {
            if (isDropdownOpen) {
                document.removeEventListener("keydown", keyFunctions, false);
                document.removeEventListener("mousedown", handleClick);
            }
        };
    }, [handleClick, isDropdownOpen, keyFunctions]);
    const handleOptionSelection = (option, ev, item) => {
        ev.stopPropagation();
        setIsDropdownOpen(false);
        option.onClick && option.onClick(item);
    };
    const openDropdown = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        setIsDropdownOpen(!isDropdownOpen);
    };
    const id = tableId || uuidv4();
    const dropdownId = `${id}__table-row-${rowId}__dropdown`;
    const allOptionsHidden = useMemo(() => dropdown?.every((option) => !toCheckIfDisplayed(option, item)), [dropdown]);
    return (React.createElement("div", { className: "menu", "data-id": dataId, "data-testid": dataTestId },
        React.createElement(TableDropdownAction, { openDropdown: (ev) => openDropdown(ev), id: dropdownId, haspopup: !!dropdown, isDropdownOpen: isDropdownOpen, dataId: dataId, dataTestId: dataTestId, disabled: allOptionsHidden }),
        isDropdownOpen && (React.createElement(Dropdown, { ariaLabelledBy: dropdownId, dropdownRef: dropdownRef, dataHook: "table-dropdown-options" }, dropdown && (React.createElement(DropdownOptions, { options: dropdown, id: dropdownId, item: item, onChange: handleOptionSelection }))))));
};
export const TableDropdownAction = (props) => {
    const t = useTranslation();
    const classes = useTableDropdownClasses();
    return (React.createElement("button", { onClick: (ev) => props?.openDropdown && props?.openDropdown(ev), id: props?.id, "aria-haspopup": !!props?.haspopup, "aria-expanded": !!props?.isDropdownOpen, className: classes.button, "data-hook": "table-dropdown-button", "data-testid": props?.dataTestId && `${props?.dataTestId}-dropdownAction`, "data-id": props?.dataId && `${props?.dataId}-dropdownAction`, type: "button", disabled: props.disabled },
        React.createElement("span", { className: "sr-only" }, t("Row Menu")),
        React.createElement("span", { className: "glyphicon glyphicon-option-vertical" })));
};
export function useTableDropdownClasses() {
    const useStyles = createUseStyles({
        button: {
            backgroundColor: "transparent",
            padding: [6, 9],
            border: 0,
            fontSize: 14,
            color: THEME.palette.gray.slate.color,
            lineHeight: "1.2!important",
            display: "inline-block",
            cursor: "pointer",
            "&:active": {
                backgroundColor: THEME.palette.gray.fog.color,
                outline: 0,
            },
            "&:focus": {
                backgroundColor: THEME.palette.gray.fog.color,
                outline: "5px auto -webkit-focus-ring-color",
                outlineOffset: -2,
            },
            "&:disabled": {
                color: THEME.palette.gray.smoke.color,
            },
        },
    });
    return useStyles();
}
